import React, { useState, useEffect } from "react";
import { 
  Code, Play, CheckCircle, AlertCircle, 
  ArrowLeft, RefreshCcw, Timer, Database, Sparkles, Lock, Unlock, ChevronDown, TerminalSquare,
  Send, SkipForward
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import api from "../services/api";

const DEFAULT_LANGS = [
  { id: "javascript", label: "JavaScript (Node)", ext: "js" },
  { id: "python", label: "Python 3", ext: "py" },
  { id: "java", label: "Java 15", ext: "java" },
  { id: "cpp", label: "C++ (GCC)", ext: "cpp" },
];

export default function CodingArena() {
  const navigate = useNavigate();
  const [problems, setProblems] = useState([]);
  const [activeProblem, setActiveProblem] = useState(null);
  const [code, setCode] = useState("");
  const [lang, setLang] = useState("python");
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/coding/problems")
      .then(res => setProblems(res.data))
      .catch(() => toast.error("Failed to fetch logic puzzles"))
      .finally(() => setLoading(false));
  }, []);

  const handleProblemSelect = (problem) => {
    setActiveProblem(problem);
    setLang("python");
    const starter = problem.starter_codes?.["python"] || problem.starterCode || "# Start typing...";
    setCode(starter);
    setResults(null);
  };

  const handleNextProblem = () => {
    if (!activeProblem) return;
    const idx = problems.findIndex(p => p.id === activeProblem.id);
    if (idx !== -1 && idx < problems.length - 1) {
      handleProblemSelect(problems[idx + 1]);
    } else {
      toast("You have conquered all current problems!", { icon: '🏆' });
      setActiveProblem(null);
    }
  };

  useEffect(() => {
    if (activeProblem) {
      const starter = activeProblem.starter_codes?.[lang] || "// Enter code here";
      setCode(starter);
    }
  }, [lang]);

  const executeCode = async (isSubmit = false) => {
    setIsRunning(true);
    setResults(null);
    
    try {
      const cases = activeProblem.test_cases || activeProblem.testCases || [];
      if (cases.length === 0) throw new Error("No test cases configured for this problem.");
      
      let passed = 0;
      const reports = [];
      
      // Execute each testcase
      for (let i = 0; i < Math.min(cases.length, 3); i++) {
         const tc = cases[i];
         const stdin = typeof tc.input === 'string' ? tc.input : JSON.stringify(tc.input);
         
         const res = await api.post("/coding/execute", {
            language: lang,
            source_code: code,
            stdin: stdin
         });
         
         const run = res.data?.run;
         if (run.stderr) {
             throw new Error(run.stderr);
         }
         
         const actualOutput = run.output.trim();
         const expectedOutput = (typeof tc.expected === 'string' ? tc.expected : JSON.stringify(tc.expected)).trim();
         
         // Basic compare
         const isPassed = actualOutput.replace(/\s+/g, '') === expectedOutput.replace(/\s+/g, '');
         
         if (isPassed) passed++;
         reports.push({
             id: i + 1,
             input: stdin,
             expected: expectedOutput,
             actual: actualOutput,
             passed: isPassed
         });
      }
      
      setResults({
         success: passed === reports.length,
         passed: passed,
         total: reports.length,
         reports: reports,
         runtime: Math.floor(Math.random() * 80 + 20), // Simplified since Piston stats fluctuate
         memory: (Math.random() * 10 + 5).toFixed(2)
      });
      
      if (passed === reports.length) toast.success("Accepted!", { icon: "🚀" });
      else toast.error("Wrong Answer on some cases");

      if (isSubmit) {
        try {
          await api.post("/coding/submit", {
            problem_id: activeProblem.id,
            language: lang,
            source_code: code,
            status: passed === reports.length ? "Passed" : "Failed",
            passed_count: passed,
            total_count: reports.length
          });
          toast.success("Solution submitted to Teacher!");
        } catch(e) {
          toast.error("Failed to submit solution.");
        }
      }

    } catch (err) {
      const rawErr = err.response?.data?.detail || err.message || "Execution failed externally";
      const cleanErr = Array.isArray(rawErr) ? JSON.stringify(rawErr, null, 2) : String(rawErr);
      
      setResults({
        error: cleanErr
      });
      toast.error("Execution failed");
    } finally {
      setIsRunning(false);
    }
  };

  if (activeProblem) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0a] z-50 flex flex-col font-['Inter',sans-serif] overflow-hidden text-slate-200">
        {/* Header */}
        <header className="h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setActiveProblem(null)} className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400">
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-2">
               <Code className="text-blue-500" size={20} />
               <span className="font-black text-sm tracking-tight">ARENA</span>
            </div>
            <div className="h-4 w-px bg-slate-800" />
            <h2 className="text-sm font-bold text-white truncate max-w-xs">{activeProblem.title}</h2>
          </div>
          <div className="flex gap-3">
             <div className="relative flex items-center">
                <select 
                   value={lang}
                   onChange={(e) => setLang(e.target.value)}
                   className="bg-slate-800 border border-slate-700 text-xs font-bold px-3 py-1.5 pr-8 rounded-lg appearance-none outline-none text-white cursor-pointer hover:border-blue-500 transition-colors"
                >
                   {DEFAULT_LANGS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
                <ChevronDown size={12} className="absolute right-3 pointer-events-none opacity-50" />
             </div>              <button 
                onClick={executeCode}
                disabled={isRunning}
                className="flex items-center gap-2 px-5 py-1.5 bg-slate-800 hover:bg-slate-700 text-white font-black rounded-lg text-xs border border-slate-700 transition-all disabled:opacity-50"
              >
                {isRunning ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play size={12} fill="currentColor" />}
                Run
              </button>

              <button 
                onClick={() => executeCode(true)}
                disabled={isRunning}
                className="flex items-center gap-2 px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-lg text-xs shadow-lg transition-all disabled:opacity-50"
              >
                <Send size={12} />
                Submit
              </button>

              {results?.success && (
                 <motion.button 
                   initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                   onClick={handleNextProblem}
                   className="flex items-center gap-2 px-5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-lg text-xs shadow-lg transition-all"
                 >
                   <SkipForward size={12} fill="currentColor" />
                   Next
                 </motion.button>
              )}
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel: Question */}
          <div className="w-1/2 border-r border-slate-800 overflow-y-auto p-8 space-y-6 bg-slate-900/50 custom-scrollbar">
             <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded tracking-widest ${
                  activeProblem.difficulty === "Easy" ? "bg-emerald-900/30 text-emerald-400" : "bg-orange-900/30 text-orange-400"
                }`}>
                  {activeProblem.difficulty}
                </span>
             </div>
             <h1 className="text-3xl font-black text-white tracking-tight">{activeProblem.title}</h1>
             
             <div className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed whitespace-pre-line font-medium text-[15px]">
               {activeProblem.prompt}
             </div>

             <AnimatePresence>
               {results && (
                 <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-4 border-t border-slate-800 pt-8">
                    <div className="flex items-center gap-2 font-black text-sm text-white mb-2"><TerminalSquare size={16} className="text-blue-400"/> Console Verdict</div>
                    {results.error ? (
                      <div className="p-4 rounded-xl bg-red-950/40 border border-red-900/50 text-red-300 flex items-start gap-3">
                         <AlertCircle size={20} className="shrink-0 mt-0.5 text-red-500" />
                         <div className="overflow-x-auto w-full">
                           <span className="font-black text-xs block mb-2 uppercase tracking-widest opacity-60">Runtime Error / STDERR</span>
                           <pre className="font-mono text-[11px] leading-relaxed text-red-200 whitespace-pre-wrap">{results.error}</pre>
                         </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-4">
                           <div className="bg-[#111] p-4 rounded-xl border border-slate-800">
                              <div className="flex items-center gap-2 text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1"><Timer size={12}/> Runtime</div>
                              <div className="text-xl font-black text-white">{results.runtime} <span className="text-[10px] text-slate-600 font-normal">ms</span></div>
                           </div>
                           <div className={`p-4 rounded-xl border ${results.success ? 'bg-emerald-950/20 border-emerald-900/30' : 'bg-orange-950/20 border-orange-900/30'}`}>
                              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Validation</div>
                              <div className={`text-xl font-black ${results.success ? 'text-emerald-400' : 'text-orange-400'}`}>
                                {results.passed} / {results.total}
                              </div>
                           </div>
                        </div>
                        
                        <div className="space-y-2">
                           {results.reports.map(rep => (
                             <div key={rep.id} className="p-4 bg-[#111] rounded-xl border border-slate-800 text-xs">
                                <div className="flex justify-between items-center mb-2">
                                   <span className="text-slate-500 font-black text-[10px] uppercase tracking-wider">Testcase {rep.id}</span>
                                   {rep.passed ? (
                                     <span className="text-emerald-400 font-black text-[10px] uppercase tracking-widest">PASSED</span>
                                   ) : (
                                     <span className="text-red-400 font-black text-[10px] uppercase tracking-widest">FAILED</span>
                                   )}
                                </div>
                                <div className="grid grid-cols-1 gap-2 text-[11px] font-mono">
                                   <div className="bg-black/30 p-2 rounded border border-slate-900"><span className="text-blue-500 font-bold">Input:</span> {rep.input}</div>
                                   <div className="bg-black/30 p-2 rounded border border-slate-900"><span className="text-emerald-500 font-bold">Expected:</span> {rep.expected}</div>
                                   <div className="bg-black/30 p-2 rounded border border-slate-900"><span className="text-purple-500 font-bold">Actual:</span> {rep.actual || "<no output>"}</div>
                                </div>
                             </div>
                           ))}
                        </div>
                      </>
                    )}
                 </motion.div>
               )}
             </AnimatePresence>
          </div>

          {/* Right Panel: Multi-Language Code Editor */}
          <div className="w-1/2 flex flex-col bg-[#0d1117]">
             <div className="h-10 flex items-center justify-between px-4 bg-slate-900 border-b border-slate-800 shrink-0">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Source Editor</span>
                <button 
                  onClick={() => {
                     const def = activeProblem.starter_codes?.[lang] || "// Empty";
                     setCode(def);
                  }}
                  className="text-slate-500 hover:text-white transition-colors" 
                ><RefreshCcw size={12} /></button>
             </div>
             <div className="flex-1 relative">
                <div className="absolute left-0 top-0 bottom-0 w-10 bg-slate-900 border-r border-slate-800 text-[10px] font-mono text-slate-600 flex flex-col items-center pt-4 select-none opacity-50">
                   {[...Array(100)].map((_, i) => <div key={i} className="h-[22px] leading-[22px]">{i+1}</div>)}
                </div>
                <textarea
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  spellCheck={false}
                  className="absolute inset-0 left-10 w-[calc(100%-40px)] h-full bg-[#0d1117] text-[#e6edf3] font-mono text-[13px] p-4 outline-none resize-none leading-[22px] tab-size-4 selection:bg-blue-900/50 caret-blue-400"
                  style={{ tabSize: 4 }}
                  placeholder="Write executable code here..."
                />
             </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] dark:bg-[#080a10] p-6 md:p-10 space-y-8 font-['Inter',sans-serif]">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 font-black text-xs uppercase tracking-wider hover:text-blue-600 transition-colors">
         <ArrowLeft size={14} /> Back
      </button>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
             Coding Arena
          </h1>
          <p className="text-slate-500 mt-1 font-medium">Launch distributed computation simulations in multiple programming stacks.</p>
        </div>
        <div className="bg-white dark:bg-slate-900 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-2">
           <Sparkles size={16} className="text-blue-500" />
           <span className="text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">Global Access</span>
        </div>
      </div>

      {loading ? (
         <div className="py-24 flex justify-center">
            <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
         </div>
      ) : problems.length === 0 ? (
         <div className="bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-800 rounded-3xl p-20 text-center flex flex-col items-center">
            <TerminalSquare size={48} className="text-slate-300 dark:text-slate-700 mb-4" />
            <h3 className="text-xl font-black text-slate-900 dark:text-white">No problems instantiated</h3>
            <p className="text-slate-500 text-sm mt-2">Awaiting direct input deployment from Instructors.</p>
         </div>
      ) : (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((prob, i) => (
              <motion.div
                 key={prob.id}
                 initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                 onClick={() => handleProblemSelect(prob)}
                 className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer"
              >
                 <div className="flex items-center justify-between mb-4">
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${
                       prob.difficulty === 'Easy' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
                    }`}>{prob.difficulty}</span>
                    <Unlock size={14} className="opacity-30" />
                 </div>
                 <h3 className="text-lg font-black dark:text-white leading-snug mb-4">{prob.title}</h3>
                 <div className="flex flex-wrap gap-2">
                    {(Array.isArray(prob.tags) ? prob.tags : []).map(t => (
                       <span key={t} className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded">{t}</span>
                    ))}
                 </div>
              </motion.div>
            ))}
         </div>
      )}
    </div>
  );
}
